/** 데일리 노트 보기 잠금 — 유저별 LS 캐시 + DB(`daily_note_view_lock`). 본문은 암호화하지 않고 UI만 가린다. */

const SCHEMA = 'v1';

export const DAILY_NOTE_VIEW_LOCK_USER_META_KEY = 'cp_daily_note_view_lock';
export const DAILY_NOTE_VIEW_LOCK_CHANGED_EVENT = 'cp:daily-note-view-lock-changed';
export const DAILY_NOTE_VIEW_SESSION_UNLOCKED_EVENT = 'cp:daily-note-view-session-unlocked';

export type DailyNoteViewLockState = {
  enabled: boolean;
  passwordHash: string;
  /** 로컬·원격 충돌 시 최신 쪽을 고르기 위한 ms */
  updatedAt: number;
};

export type DailyNoteViewLockChangedDetail = {
  userId: string;
  enabled: boolean;
};

function emptyLock(): DailyNoteViewLockState {
  return { enabled: false, passwordHash: '', updatedAt: 0 };
}

function storageKey(userId: string): string {
  return `cp.dailyNote.viewLock.${SCHEMA}.${userId}`;
}

export function isDailyNoteViewLockStorageKey(key: string): boolean {
  return key.startsWith(`cp.dailyNote.viewLock.${SCHEMA}.`);
}

function normalizeLock(state: Partial<DailyNoteViewLockState> | null | undefined): DailyNoteViewLockState {
  const passwordHash = typeof state?.passwordHash === 'string' ? state.passwordHash : '';
  const updatedAt =
    typeof state?.updatedAt === 'number' && Number.isFinite(state.updatedAt) && state.updatedAt > 0
      ? state.updatedAt
      : 0;
  return {
    enabled: Boolean(state?.enabled) && Boolean(passwordHash),
    passwordHash,
    updatedAt,
  };
}

function locksEqual(a: DailyNoteViewLockState, b: DailyNoteViewLockState): boolean {
  return a.enabled === b.enabled && a.passwordHash === b.passwordHash && a.updatedAt === b.updatedAt;
}

function utf8Bytes(text: string): Uint8Array {
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(text);
  }
  const bytes: number[] = [];
  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i);
    if (code < 128) bytes.push(code);
    else {
      const encoded = encodeURIComponent(text[i] ?? '');
      for (let j = 0; j < encoded.length; j += 1) {
        if (encoded[j] === '%') {
          bytes.push(Number.parseInt(encoded.slice(j + 1, j + 3), 16));
          j += 2;
        } else {
          bytes.push(encoded.charCodeAt(j));
        }
      }
    }
  }
  return Uint8Array.from(bytes);
}

async function sha256Hex(text: string): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) {
    let h = 2166136261;
    for (let i = 0; i < text.length; i += 1) {
      h ^= text.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return `fnv1a-${(h >>> 0).toString(16).padStart(8, '0')}`;
  }
  const buf = await subtle.digest('SHA-256', utf8Bytes(text));
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export async function hashDailyNoteLockPassword(userId: string, password: string): Promise<string> {
  const uid = userId.trim();
  const pw = password.trim();
  if (!uid || !pw) return '';
  return sha256Hex(`cp.dailyNote.viewLock:${uid}:${pw}`);
}

export async function dailyNoteLockPasswordMatches(
  userId: string,
  password: string,
  passwordHash: string
): Promise<boolean> {
  if (!passwordHash) return false;
  const hashed = await hashDailyNoteLockPassword(userId, password);
  return hashed === passwordHash;
}

export function parseDailyNoteViewLockMeta(
  metadata: Record<string, unknown> | null | undefined
): DailyNoteViewLockState | null {
  const raw = metadata?.[DAILY_NOTE_VIEW_LOCK_USER_META_KEY];
  if (!raw || typeof raw !== 'object') return null;
  const rec = raw as Record<string, unknown>;
  const passwordHash = typeof rec.h === 'string' ? rec.h : '';
  const updatedAt =
    typeof rec.u === 'number' && Number.isFinite(rec.u) && rec.u > 0
      ? rec.u
      : typeof rec.u === 'string'
        ? Number.parseInt(rec.u, 10) || 0
        : 0;
  const enabledFlag = rec.e === 1 || rec.e === true;
  return {
    enabled: Boolean(enabledFlag) && Boolean(passwordHash),
    passwordHash,
    updatedAt: Number.isFinite(updatedAt) && updatedAt > 0 ? updatedAt : 0,
  };
}

export function dailyNoteViewLockToUserMetaPayload(state: DailyNoteViewLockState): {
  e: 0 | 1;
  h: string;
  u: number;
} {
  const enabled = Boolean(state.enabled) && Boolean(state.passwordHash);
  return {
    e: enabled ? 1 : 0,
    h: state.passwordHash,
    u: state.updatedAt > 0 ? state.updatedAt : Date.now(),
  };
}

export function dailyNoteViewLockFromDbRow(row: {
  enabled?: boolean | null;
  password_hash?: string | null;
  updated_at?: string | null;
} | null): DailyNoteViewLockState | null {
  if (!row) return null;
  const passwordHash = typeof row.password_hash === 'string' ? row.password_hash : '';
  const parsedAt = row.updated_at ? Date.parse(row.updated_at) : NaN;
  return {
    enabled: Boolean(row.enabled) && Boolean(passwordHash),
    passwordHash,
    updatedAt: Number.isFinite(parsedAt) && parsedAt > 0 ? parsedAt : 0,
  };
}

export function dailyNoteViewLockToDbPayload(
  userId: string,
  state: DailyNoteViewLockState
): {
  user_id: string;
  enabled: boolean;
  password_hash: string;
  updated_at: string;
} {
  const enabled = Boolean(state.enabled) && Boolean(state.passwordHash);
  const updatedAt = state.updatedAt > 0 ? state.updatedAt : Date.now();
  return {
    user_id: userId.trim(),
    enabled,
    password_hash: state.passwordHash,
    updated_at: new Date(updatedAt).toISOString(),
  };
}

export function mergeDailyNoteViewLock(
  local: DailyNoteViewLockState,
  remote: DailyNoteViewLockState | null
): DailyNoteViewLockState {
  if (!remote) return local;
  const lu = local.updatedAt || 0;
  const ru = remote.updatedAt || 0;
  if (ru > lu) return remote;
  if (lu > ru) return local;
  if (remote.enabled && !local.enabled) return remote;
  return local;
}

function emitDailyNoteViewLockChanged(userId: string, state: DailyNoteViewLockState): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(
      new CustomEvent<DailyNoteViewLockChangedDetail>(DAILY_NOTE_VIEW_LOCK_CHANGED_EVENT, {
        detail: { userId, enabled: state.enabled },
      })
    );
  } catch {
    /* noop */
  }
}

/** 이번 JS 세션에서만 보기 잠금 해제. 새로고침하면 다시 잠긴다. HMR에도 같은 window 키를 쓴다. */
const SESSION_UNLOCK_WINDOW_KEY = '__cpDailyNoteViewSessionUnlockedUserId';

type SessionUnlockWindow = Window & { [SESSION_UNLOCK_WINDOW_KEY]?: string };

function readSessionUnlockedUserId(): string {
  if (typeof window === 'undefined') return '';
  return (window as SessionUnlockWindow)[SESSION_UNLOCK_WINDOW_KEY] ?? '';
}

function writeSessionUnlockedUserId(userId: string): void {
  if (typeof window === 'undefined') return;
  (window as SessionUnlockWindow)[SESSION_UNLOCK_WINDOW_KEY] = userId;
}

export function isDailyNoteViewSessionUnlocked(userId: string): boolean {
  const uid = userId.trim();
  return Boolean(uid) && readSessionUnlockedUserId() === uid;
}

export function setDailyNoteViewSessionUnlocked(userId: string, unlocked: boolean): void {
  const uid = userId.trim();
  const next = unlocked && uid ? uid : '';
  writeSessionUnlockedUserId(next);
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(
      new CustomEvent(DAILY_NOTE_VIEW_SESSION_UNLOCKED_EVENT, {
        detail: { userId: uid, unlocked: Boolean(next) },
      })
    );
  } catch {
    /* noop */
  }
}

export function loadDailyNoteViewLock(userId: string): DailyNoteViewLockState {
  const uid = userId.trim();
  if (!uid || typeof window === 'undefined') return emptyLock();
  try {
    const raw = window.localStorage.getItem(storageKey(uid));
    if (!raw) return emptyLock();
    const parsed = JSON.parse(raw) as Partial<DailyNoteViewLockState>;
    return normalizeLock(parsed);
  } catch {
    return emptyLock();
  }
}

export function saveDailyNoteViewLock(userId: string, state: DailyNoteViewLockState): DailyNoteViewLockState {
  const uid = userId.trim();
  const next = normalizeLock({
    enabled: state.enabled,
    passwordHash: state.passwordHash,
    updatedAt: state.updatedAt > 0 ? state.updatedAt : Date.now(),
  });
  if (!uid || typeof window === 'undefined') return next;
  try {
    window.localStorage.setItem(storageKey(uid), JSON.stringify(next));
  } catch {
    /* quota */
  }
  emitDailyNoteViewLockChanged(uid, next);
  return next;
}

/** LS를 먼저 쓰고, DB 원격이 더 최신이면 맞춘다. */
export function hydrateDailyNoteViewLock(
  userId: string,
  remote: DailyNoteViewLockState | null
): DailyNoteViewLockState {
  const local = loadDailyNoteViewLock(userId);
  const merged = mergeDailyNoteViewLock(local, remote);
  if (!locksEqual(merged, local)) {
    return saveDailyNoteViewLock(userId, merged);
  }
  return merged;
}

export function dailyNoteViewLockNeedsRemoteSync(
  local: DailyNoteViewLockState,
  remote: DailyNoteViewLockState | null
): boolean {
  if (!remote) return Boolean(local.enabled || local.passwordHash);
  return (
    local.enabled !== remote.enabled ||
    local.passwordHash !== remote.passwordHash ||
    (local.updatedAt || 0) > (remote.updatedAt || 0)
  );
}
